import type { ReactNode } from "react";

type KnownProvider = "google" | "facebook" | "github" | "microsoft";

interface ConnectedAccountProviderProps {
  provider: string;
  title: ReactNode;
  description: ReactNode;
}

const GOOGLE_ICON = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g fill="none" fillRule="evenodd">
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </g>
  </svg>
);

const FACEBOOK_ICON = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 10C20 4.477 15.523 0 10 0S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z"
      fill="#1877F2"
    />
  </svg>
);

const PROVIDER_ICONS: Record<KnownProvider, ReactNode> = {
  google: GOOGLE_ICON,
  facebook: FACEBOOK_ICON,
  github: <span className="text-sm font-semibold uppercase text-slate-600 dark:text-slate-400">GH</span>,
  microsoft: <span className="text-sm font-semibold uppercase text-slate-600 dark:text-slate-400">MS</span>
};

const KNOWN_PROVIDERS: KnownProvider[] = ["google", "facebook", "github", "microsoft"];
const KNOWN_PROVIDER_SET = new Set<KnownProvider>(KNOWN_PROVIDERS);
const providerContainerClasses = "flex w-full items-center gap-4";
const providerIconBaseClasses =
  "flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800";
const fallbackIconClasses = "text-sm font-semibold uppercase text-slate-600 dark:text-slate-400";
const providerDetailsClasses =
  "flex flex-1 flex-col gap-1 text-sm text-slate-500 dark:text-slate-400";

export function ConnectedAccountProvider({
  provider,
  title,
  description
}: ConnectedAccountProviderProps) {
  const normalized = provider.toLowerCase();
  const knownProvider = KNOWN_PROVIDER_SET.has(normalized as KnownProvider)
    ? (normalized as KnownProvider)
    : null;
  const icon = knownProvider ? PROVIDER_ICONS[knownProvider] : null;
  const fallbackLabel = provider.charAt(0).toUpperCase() || "?";
  const iconClassNames = [providerIconBaseClasses];
  const detailsHeadingClasses = "text-base font-semibold text-slate-900 dark:text-slate-100";

  return (
    <div className={providerContainerClasses}>
      <span className={iconClassNames.join(" ")} aria-hidden="true">
        {icon ?? <span className={fallbackIconClasses}>{fallbackLabel}</span>}
      </span>
      <div className={providerDetailsClasses}>
        <h3 className={detailsHeadingClasses}>{title}</h3>
        <p>{description}</p>
      </div>
    </div>
  );
}
